import { useEffect, useRef, useState, useCallback } from 'react';
import { getBackendOrigin } from '@/lib/apiBase';

const RECONNECT_DELAY_MS = 3000;
const PING_INTERVAL_MS = 25000;

/**
 * Persistent WebSocket connection with auto-reconnect and JWT auth.
 *
 * @param {string|null} token  - JWT access token; pass null to stay disconnected
 * @param {function}    onMessage - called with parsed JSON event on each server message
 * @returns {{ connected: boolean, sendMessage: function }}
 */
export function useWebSocket(token, onMessage) {
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const pingTimerRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  const [connected, setConnected] = useState(false);
  const mountedRef = useRef(true);

  // Keep onMessage ref fresh without causing reconnects
  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);

  const clearTimers = useCallback(() => {
    clearTimeout(reconnectTimerRef.current);
    clearInterval(pingTimerRef.current);
  }, []);

  const connect = useCallback(() => {
    if (!token || !mountedRef.current) return;

    const backendUrl = getBackendOrigin();
    const wsBase = backendUrl
      ? backendUrl.replace(/^https/, 'wss').replace(/^http/, 'ws')
      : `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`;
    const wsUrl = `${wsBase}/ws?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) { ws.close(); return; }
        setConnected(true);
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, PING_INTERVAL_MS);
      };

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type !== 'pong' && msg.type !== 'ping') {
            onMessageRef.current?.(msg);
          }
        } catch {}
      };

      ws.onerror = () => {};

      ws.onclose = () => {
        clearInterval(pingTimerRef.current);
        setConnected(false);
        if (mountedRef.current && token) {
          reconnectTimerRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
        }
      };
    } catch {}
  }, [token, clearTimers]);

  useEffect(() => {
    mountedRef.current = true;
    if (token) {
      connect();
    }
    return () => {
      mountedRef.current = false;
      clearTimers();
      wsRef.current?.close();
    };
  }, [token, connect, clearTimers]);

  const sendMessage = useCallback((msg) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  return { connected, sendMessage };
}
